import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Users,
  ChevronRight,
  Filter,
  Search,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';

interface HistoryClass {
  id: string;
  name: string;
  date: string;
  time: string;
  enrolled: Record<string, any>;
  maxCapacity: number;
  location?: string;
  attendance?: Record<string, boolean>;
}

const InstructorHistory: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<HistoryClass[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<HistoryClass[]>([]);
  const [instructorId, setInstructorId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'week' | 'month' | 'all'>('month');

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorId();
    }
  }, [businessId, currentUser]);

  useEffect(() => {
    if (instructorId) {
      loadHistory();
    }
  }, [instructorId]);

  useEffect(() => {
    filterClasses();
  }, [classes, searchTerm, dateFilter]);

  const loadInstructorId = async () => {
    if (!businessId || !currentUser) return;

    try {
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const snapshot = await getDocs(instructorsQuery);

      if (!snapshot.empty) {
        setInstructorId(snapshot.docs[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error cargando instructor:', error);
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!businessId || !instructorId) return;

    try {
      setLoading(true);

      // Formato local YYYY-MM-DD sin conversión a UTC
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      // Cargar todas las clases pasadas
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('instructorId', '==', instructorId)
      );
      const snapshot = await getDocs(classesQuery);

      const allClasses = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as HistoryClass[];

      // Filtrar solo clases pasadas y ordenar por fecha descendente
      const pastClasses = allClasses
        .filter(c => c.date < today || (c.date === today && c.time < new Date().toTimeString().substring(0, 5)))
        .sort((a, b) => {
          if (a.date === b.date) return b.time.localeCompare(a.time);
          return b.date.localeCompare(a.date);
        });

      setClasses(pastClasses);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterClasses = () => {
    let result = [...classes];

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        c.name.toLowerCase().includes(term) ||
        c.location?.toLowerCase().includes(term)
      );
    }

    // Filtrar por fecha
    const now = new Date();
    if (dateFilter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      result = result.filter(c => new Date(c.date) >= weekAgo);
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      result = result.filter(c => new Date(c.date) >= monthAgo);
    }

    setFilteredClasses(result);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const formatTime = (time: string) => time.substring(0, 5);

  const getAttendanceRate = (classItem: HistoryClass) => {
    const enrolled = Object.keys(classItem.enrolled || {}).length;
    if (enrolled === 0) return 0;
    const present = Object.values(classItem.attendance || {}).filter(Boolean).length;
    return Math.round((present / enrolled) * 100);
  };

  const getTotalStats = () => {
    const total = filteredClasses.length;
    const totalStudents = filteredClasses.reduce((sum, c) => sum + Object.keys(c.enrolled || {}).length, 0);
    const totalAttendance = filteredClasses.reduce((sum, c) => {
      return sum + Object.values(c.attendance || {}).filter(Boolean).length;
    }, 0);
    const avgAttendance = totalStudents > 0 ? Math.round((totalAttendance / totalStudents) * 100) : 0;

    return { total, totalStudents, avgAttendance };
  };

  const stats = getTotalStats();

  if (!instructorId && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Cuenta no vinculada</h2>
          <p className="text-gray-400">
            Tu cuenta no está vinculada a un perfil de instructor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Historial de Clases</h1>
        <p className="text-gray-400">Todas las clases que has impartido</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-primary-400">{stats.total}</div>
          <div className="text-sm text-gray-500">Clases</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{stats.totalStudents}</div>
          <div className="text-sm text-gray-500">Alumnos</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{stats.avgAttendance}%</div>
          <div className="text-sm text-gray-500">Asistencia</div>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar clase..."
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={dateFilter === 'week' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('week')}
          >
            Semana
          </Button>
          <Button
            variant={dateFilter === 'month' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('month')}
          >
            Mes
          </Button>
          <Button
            variant={dateFilter === 'all' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('all')}
          >
            Todo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      ) : filteredClasses.length === 0 ? (
        <Card className="p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No hay clases en el historial</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredClasses.map((classItem) => {
            const enrolledCount = Object.keys(classItem.enrolled || {}).length;
            const attendanceRate = getAttendanceRate(classItem);
            const presentCount = Object.values(classItem.attendance || {}).filter(Boolean).length;
            const hasAttendance = Object.keys(classItem.attendance || {}).length > 0;

            return (
              <Card
                key={classItem.id}
                className="p-4 cursor-pointer hover:border-primary-500/50 transition-all"
                onClick={() => navigate(`/instructor/class/${classItem.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{classItem.name}</h3>
                      {hasAttendance ? (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          attendanceRate >= 80 
                            ? 'bg-green-500/20 text-green-400' 
                            : attendanceRate >= 50 
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-red-500/20 text-red-400'
                        }`}>
                          {attendanceRate}% asistencia
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-400">
                          Sin registro
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(classItem.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(classItem.time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {hasAttendance ? (
                          <>
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            {presentCount}
                            <XCircle className="w-3 h-3 text-red-400 ml-1" />
                            {enrolledCount - presentCount}
                          </>
                        ) : (
                          <>{enrolledCount} inscritos</>
                        )}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InstructorHistory;










